/**
 * Друкована форма «Акт приймання давальницької сировини» (довільна форма,
 * ч.687): ми = ВИКОНАВЕЦЬ, сировина замовника на позабалансі 022.
 * Замовник + договір, таблиця сировини зі складом ПО РЯДКАХ (0465) і
 * задекларованою вартістю ЗАМОВНИКА (його оцінка — друкується завжди,
 * це база матвідповідальності виконавця); підписи. Канон друку ч.669/677.
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { materialReceiptsForProcessing } from '@/api/materialReceiptsForProcessing';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import PrintActions from '@/shared/components/PrintActions';
import { contractLabel } from '@/shared/utils/labels';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmtQty = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { maximumFractionDigits: 3 });
const fmtMoney = (n: number | string | null | undefined) =>
  Number(n ?? 0).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function MaterialReceiptPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['material-receipt-for-processing', id],
    queryFn: () => materialReceiptsForProcessing.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const cQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll() });
  const contractsQ = useQuery({
    queryKey: ['contracts', d?.organization_id, d?.customer_contractor_id],
    queryFn: () => refs.contracts({
      organization_id: d!.organization_id,
      contractor_id: d!.customer_contractor_id,
    }),
    enabled: !!d?.organization_id && !!d?.customer_contractor_id,
  });
  const whQ = useQuery({
    queryKey: ['warehouses', d?.organization_id],
    queryFn: () => refs.warehouses(d!.organization_id),
    enabled: !!d?.organization_id,
  });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження акта…</div>;
  }

  const whName = new Map((whQ.data ?? []).map((w) => [w.id, w.name]));
  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  const customer = cQ.data?.items.find((c) => c.id === d.customer_contractor_id);
  const contract = (contractsQ.data ?? []).find((c) => c.id === d.contract_id);
  // ч.687: склад рядка > склад шапки (legacy); колонка — при >1 ефективному.
  const effWh = (it: { warehouse_id?: string | null }) =>
    it.warehouse_id || d.destination_warehouse_id || '';
  const multiWarehouse = new Set(d.items.map(effWh).filter(Boolean)).size > 1;
  const singleWhId = d.items.map(effWh).find(Boolean) ?? '';
  const uomOf = (it: { nomenclature_id: string; uom_id?: string | null }) => {
    if (it.uom_id) return uomById.get(it.uom_id) ?? 'шт';
    const n = nomById.get(it.nomenclature_id);
    return (n?.base_uom_id && uomById.get(n.base_uom_id)) || 'шт';
  };
  const totalDeclared = d.items.reduce((s, it) => s + Number(it.declared_value ?? 0), 0);

  return (
    <div className="print-page" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .mrp-table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 9pt; line-height: 1.15; }
        .mrp-table th, .mrp-table td { border: 1px solid #000; padding: 1px 4px; }
        .mrp-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.material_receipt_for_processing',
        documentId: d.id,
        filename: `Прийняття сировини ${d.document_number}`,
        orgName: org.name,
      }} />

      <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
        <div style={{ fontSize: '10pt' }}>{org.full_name || org.name}</div>
        {partyCodesText(org) && <div style={{ fontSize: '10pt', color: '#555' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0' }}>
        Акт приймання давальницької сировини № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>

      <table style={{ width: '100%', fontSize: '10pt', marginBottom: '0.5rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '55%' }}>
              <span style={{ color: '#555' }}>Замовник (власник сировини): </span>
              <strong>{customer?.name ?? '—'}</strong>
              {partyCodesText(customer) && <span style={{ color: '#555' }}> ({partyCodesText(customer)})</span>}
            </td>
            <td>
              <span style={{ color: '#555' }}>Договір: </span>
              {contract ? contractLabel(contract) : 'без договору'}
            </td>
          </tr>
          <tr>
            <td colSpan={2}>
              <span style={{ color: '#555' }}>Склад розміщення: </span>
              {multiWarehouse
                ? 'за рядками (див. таблицю)'
                : (whName.get(singleWhId) ?? '—')}
            </td>
          </tr>
        </tbody>
      </table>

      <table className="mrp-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Сировина (найменування)</th>
            {multiWarehouse && <th style={{ width: 130 }}>Склад</th>}
            <th style={{ width: 45 }}>Од.</th>
            <th style={{ width: 80 }}>К-сть</th>
            <th style={{ width: 110 }}>Задекл. вартість</th>
          </tr>
        </thead>
        <tbody>
          {d.items.map((it, i) => {
            const nom = nomById.get(it.nomenclature_id);
            return (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nom?.name ?? '—'}{nom?.code && <span style={{ color: '#555' }}> ({nom.code})</span>}</td>
                {multiWarehouse && <td>{whName.get(effWh(it)) ?? '—'}</td>}
                <td style={{ textAlign: 'center' }}>{uomOf(it)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(it.quantity)}</td>
                <td style={{ textAlign: 'right' }}>
                  {it.declared_value != null ? fmtMoney(it.declared_value) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: 700 }}>
            <td colSpan={multiWarehouse ? 5 : 4} style={{ textAlign: 'right' }}>
              Разом задекларована вартість:
            </td>
            <td style={{ textAlign: 'right' }}>{fmtMoney(totalDeclared)}</td>
          </tr>
        </tfoot>
      </table>

      <p style={{ margin: '0.5rem 0' }}>
        Всього найменувань {d.items.length}, задекларована вартість{' '}
        <strong>{fmtMoney(totalDeclared)} грн</strong>. Сировина прийнята на
        давальницьких умовах і залишається власністю замовника; виконавець
        несе матеріальну відповідальність у межах задекларованої вартості.
      </p>
      {d.comment && <p style={{ margin: '0.5rem 0', color: '#333' }}>Коментар: {d.comment}</p>}

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Передав (представник замовника)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б., довіреність</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Прийняв (МВО складу)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
